import React, {
  forwardRef,
  MouseEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { ICellRendererReactComp } from "ag-grid-react";
import { KeyboardArrowDown } from "@mui/icons-material";
import ArrowPopper from "components/app/ArrowPopper";
import { Divider, Grid } from "@mui/material";
import InvoiceTotal from "../components/InvoiceTotal";
import { AOV_SUB_GROUP_MAPPING, SUB_GROUP_MAPPING } from "constant";
import { classHOC } from "components/app/ClassWrapper";
//import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";

const StaticCollectionRevenueCellRenderer = forwardRef<
  ICellRendererReactComp,
  ICellRendererParams & { collection: string[]; type?: string }
>(({ data, api, collection, type = "arr", value, node, column }, ref) => {
  useImperativeHandle(ref, () => ({
    refresh: () => false,
  }));

  if (node.rowPinned) {
    return <InvoiceTotal {...{ column, api, data }} />;
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
  const [groupColumns, setGroupColumns] = useState<Partial<ColDef & ColGroupDef>[]>([]);

  // const showBlank = data.pendingCount === data.subscriptionCount && !data.new;
  const columns = useMemo(() => {
    return collection!.map((x) => api.getColumnDef(x));
  }, [collection]);

  useEffect(() => {
    const result: Partial<ColDef & ColGroupDef>[] = [];
    const groupPart: string[] = [];
    columns?.forEach((item: any) => {
      if (item?.subGroupHeader && item?.subGroupHeader !== "Normal") {
        if (groupPart.includes(item.subGroupHeader)) {
          const curItem = result.filter((r) => r.headerName === item?.subGroupHeader);
          if (curItem?.length) {
            curItem?.[0]?.children?.push({
              colId: item?.colId,
              headerName: item?.headerName,
            });
          }
        } else {
          const curItem: any = {
            colId: (type === "arr" ? SUB_GROUP_MAPPING : AOV_SUB_GROUP_MAPPING).filter(
              (i) => i.groupHeader === item?.subGroupHeader
            )?.[0]?.colId,
            headerName: item?.subGroupHeader,
            children: [],
          };
          curItem.children.push({
            colId: item?.colId,
            headerName: item?.headerName,
          });
          groupPart.push(item.subGroupHeader);
          result.push(curItem);
        }
      } else {
        if (
          (type === "arr" ? SUB_GROUP_MAPPING : AOV_SUB_GROUP_MAPPING).find(
            (i) => i.colId !== item?.colId
          )
        )
          result.push({
            colId: item?.colId,
            headerName: item?.headerName,
          });
      }
    });
    setGroupColumns(result);
  }, [columns]);

  const popRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const closePop = (e: Event) => {
    if (
      !popRef.current?.contains(e.target as Node) &&
      popRef.current !== e.target &&
      !rootRef.current?.contains(e.target as Node)
    ) {
      setAnchorEl(null);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  // if (showBlank) return <></>;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
        ref={rootRef}
        onClick={column.getColId() === "aovNonCollab" ? undefined : handleClick}
      >
        {value}
        {column.getColId() === "aovNonCollab" ? null : (
          <KeyboardArrowDown style={{ fontSize: "1rem" }} />
        )}
      </div>
      {anchorEl ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="right"
          disablePortal={false}
          style={{ zIndex: status ? 0 : 2000 }}
        >
          <div ref={popRef}>
            <Grid
              container={true}
              direction="column"
            >
              {groupColumns.length ? (
                groupColumns.map((col: Partial<ColDef & ColGroupDef>, i) => (
                  <div key={col?.colId}>
                    {!!col?.children?.length && <Divider style={{ margin: "5px 0" }} />}
                    <Grid
                      item={true}
                      container={true}
                      key={col.colId!}
                      justifyContent="space-between"
                      style={{ marginTop: i === 0 ? 0 : 4 }}
                      alignItems="center"
                    >
                      <Grid
                        item={true}
                        style={{
                          color: "#545454",
                          fontSize: 14,
                          lineHeight: "19px",
                        }}
                      >
                        {col.headerName!.split(/\((.*)\)/)?.[1]}
                      </Grid>
                      <Grid
                        item={true}
                        style={{
                          color: "#333",
                          fontSize: 14,
                          lineHeight: "19px",
                          marginLeft: 16,
                          fontWeight: 500,
                        }}
                      >
                        $
                        {((data[col.field! || col.colId!] as number) || 0).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Grid>
                    </Grid>
                    {!!col?.children?.length && (
                      <Grid
                        item={true}
                        container={true}
                        key={col?.colId}
                        justifyContent="space-between"
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          marginTop: i === 0 ? 0 : 4,
                        }}
                        alignItems="center"
                      >
                        {col?.children?.map((child: Partial<ColDef & ColGroupDef>) => (
                          <div
                            key={child.colId!}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <Grid
                              item={true}
                              style={{
                                color: "#898989",
                                fontSize: 12,
                                lineHeight: "16px",
                              }}
                            >
                              {child.headerName!.split(/\((.*)\)/)?.[1]}
                            </Grid>
                            <Grid
                              item={true}
                              style={{
                                color: "#333",
                                fontSize: 12,
                                lineHeight: "14px",
                                marginLeft: 16,
                                fontWeight: 500,
                              }}
                            >
                              $
                              {((data[child.field! || child.colId!] as number) || 0).toLocaleString(
                                "en-US",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </Grid>
                          </div>
                        ))}
                      </Grid>
                    )}
                    {!!col?.children?.length && <Divider style={{ margin: "5px 0" }} />}
                  </div>
                ))
              ) : (
                <span>No column selected</span>
              )}
            </Grid>
          </div>
        </ArrowPopper>
      ) : null}
    </>
  );
});

export default classHOC(StaticCollectionRevenueCellRenderer);
